<script setup lang="ts">
import { HttpStatusCode, isAxiosError } from 'axios';
import type { ColorVariant, TableField, TableItem } from 'bootstrap-vue-next';
import { BButton, BLink, BBadge } from 'bootstrap-vue-next';
import { ref, computed, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import SearchSvg from '@images/empty-search-md.svg';
import ErrorSvg from '@images/status-fail-md.svg';

import type { GetApiV1UsersByIdResponse } from '~/api/generated';
import { fetchUserProfile } from '~/api/users';
import ContributorLevelBadge from '~/common/components/ContributorLevelBadge.vue';
import FilterCard from '~/common/components/FilterCard.vue';
import IssueRewardModal from '~/common/components/IssueRewardModal.vue';
import LevelProgress from '~/common/components/LevelProgress.vue';
import MarkdownLinkRenderer from '~/common/components/MarkdownLinkRenderer.vue';
import PointsDocumentationTooltip from '~/common/components/PointsDocumentationTooltip.vue';
import { BONUS_POINT_ACTIVITY_TYPES, DEFAULT_TITLE, REWARD_REASONS } from '~/common/Constants';
import { dateFieldFormatter, numberProps, compactNumberFormatter } from '~/common/FieldFormats';
import * as ScoringUtils from '~/common/ScoringUtils';
import { useVueData } from '~/common/useVueData';

import AddContentModal from './user-page/AddContentModal.vue';
import AddEventModal from './user-page/AddEventModal.vue';
import AddPointsModal from './user-page/AddPointsModal.vue';
import MentorshipCard from './user-page/MentorshipCard.vue';
import PagedAccordionTable from './user-page/PagedAccordionTable.vue';
import SetCrowdinUsernameModal from './user-page/SetCrowdinUsernameModal.vue';
import ShareButtons from './user-page/ShareButtons.vue';

const { isAdmin, username: loggedInUsername, isCurrentUserId } = useVueData();

const data = ref<GetApiV1UsersByIdResponse | null>(null);
const busy = ref(true);
const filterData = ref({
  fromDate: '',
  toDate: '',
});
const error = ref(false);
const isUserNotFound = ref(false);
const showAddPointsModal = ref(false);
const showAddEventModal = ref(false);
const showIssueRewardModal = ref(false);
const showAddContentModal = ref(false);
const showSetCrowdinUsernameModal = ref(false);

const route = useRoute();
const router = useRouter();

const userHandle = ref(String(route.params.userHandle));
watch(() => route.params, (params, oldParams = {}) => {
  if (params['userHandle'] !== oldParams['userHandle']) {
    userHandle.value = String(params['userHandle']);
    void fetchData();
  }
});

const userId = computed(() => data.value?.id);
const ownProfile = computed(() => isCurrentUserId(userId.value));
const canManageCrowdinUsername = computed(() => (ownProfile.value || isAdmin) && Boolean(userId.value));

const profileShareText = computed(
  () => `Check out my GitLab contributor profile — Level ${data.value?.contributor_level} with ${compactNumberFormatter(data.value?.points)} points! 🦊`,
);
const profileShareUrl = computed(
  () => `${window.location.origin}/users/${data.value?.username}`,
);

const levelProgressData = computed(() => {
  return {
    contributorLevel: data.value?.contributor_level,
    contributorPoints: data.value?.points,
  };
});

type ProfileTableFields = TableField<TableItem>[];

const labelFor = (labels: Partial<Record<string, string>>, value: unknown) =>
  (typeof value === 'string' && labels[value]) || '';

const rewardFields = computed(() => {
  const fields: ProfileTableFields = [
    {
      key: 'reason',
      label: 'Reason',
      formatter: ({ value }) => labelFor(REWARD_REASONS, value),
    },
    {
      key: 'issue_web_url',
      label: 'Reward issue',
    },
    {
      key: 'credits',
      label: 'Credits',
    },
    {
      key: 'awarded_by',
      label: 'Issued by',
    },
    {
      key: 'created_at',
      label: 'Issued date',
      formatter: dateFieldFormatter,
    },
  ];

  if (ownProfile.value) {
    fields.push({ key: 'actions', label: '', sortable: false });
  }

  return fields;
});

const showEventStatusColumn = computed(() => events.value?.some(
  item => ['pending', 'rejected'].includes(item.status),
));

const eventFields = computed<ProfileTableFields>(() => [
  {
    key: 'reason',
    label: 'Details',
  },
  ...(showEventStatusColumn.value
    ? [{
        key: 'status',
        label: 'Status',
      }]
    : []),
  {
    key: 'created_at',
    label: 'Added date',
    formatter: dateFieldFormatter,
  },
  {
    key: 'points',
    label: 'Points',
    ...numberProps,
  },
]);

const SUBMISSION_STATUS_VARIANT: Partial<Record<string, ColorVariant>> = {
  pending: 'warning',
  rejected: 'danger',
  approved: 'success',
};

const bonusPointsFields: ProfileTableFields = [
  {
    key: 'activity_type',
    label: 'Activity type',
    formatter: ({ value }) => labelFor(BONUS_POINT_ACTIVITY_TYPES, value),
  },
  {
    key: 'reason',
    label: 'Reason',
  },
  {
    key: 'created_at',
    label: 'Awarded date',
    formatter: dateFieldFormatter,
  },
  {
    key: 'points',
    label: 'Points',
    ...numberProps,
  },
];

const mergedMergeRequestFields: ProfileTableFields = [
  {
    key: 'title',
    label: 'Title',
  },
  {
    key: 'opened_date',
    label: 'Open date',
    formatter: dateFieldFormatter,
  },
  {
    key: 'merged_date',
    label: 'Merge date',
    formatter: dateFieldFormatter,
  },
  {
    key: 'has_linked_issue',
    label: 'Linked issue',
  },
];

const openedMergeRequestFields: ProfileTableFields = [
  {
    key: 'title',
    label: 'Title',
  },
  {
    key: 'opened_date',
    label: 'Open date',
    formatter: dateFieldFormatter,
  },
  {
    key: 'state_id',
    label: 'State',
  },
  {
    key: 'has_linked_issue',
    label: 'Linked issue',
  },
  {
    key: 'actions',
    label: 'Actions',
    sortable: false,
  },
];

const commitFields: ProfileTableFields = [
  {
    key: 'merge_request_title',
    label: 'Merge request title',
  },
  {
    key: 'sha',
    label: 'SHA',
  },
  {
    key: 'committed_date',
    label: 'Commit date',
    formatter: dateFieldFormatter,
  },
  {
    key: 'merged_date',
    label: 'Merge date',
    formatter: dateFieldFormatter,
  },
];

const issueFields: ProfileTableFields = [
  {
    key: 'title',
    label: 'Title',
  },
  {
    key: 'opened_date',
    label: 'Open date',
    formatter: dateFieldFormatter,
  },
  {
    key: 'state_id',
    label: 'State',
  },
];

const noteFields: ProfileTableFields = [
  {
    key: 'noteable_title',
    label: 'Noteable title',
  },
  {
    key: 'added_date',
    label: 'Added date',
    formatter: dateFieldFormatter,
  },
];

const translationFields: ProfileTableFields = [
  {
    key: 'added_date',
    label: 'Added date',
  },
  {
    key: 'translated_strings',
    label: 'Translated strings',
    ...numberProps,
  },
  {
    key: 'points',
    label: 'Points',
    ...numberProps,
  },
];

const discordMessageFields: ProfileTableFields = [
  {
    key: 'added_date',
    label: 'Added date',
    formatter: dateFieldFormatter,
  },
  {
    key: 'reply',
    label: 'Reply',
  },
  {
    key: 'channel',
    label: 'Channel',
  },
];

const forumPostFields: ProfileTableFields = [
  {
    key: 'topic',
    label: 'Topic',
  },
  {
    key: 'reply',
    label: 'Reply',
  },
  {
    key: 'added_date',
    label: 'Added date',
    formatter: dateFieldFormatter,
  },
];

const activityFields: ProfileTableFields = [
  {
    key: 'occurred_at',
    label: 'Date',
    formatter: dateFieldFormatter,
  },
  {
    key: 'kind',
    label: 'Type',
  },
  {
    key: 'title',
    label: 'Description',
  },
];

const filter = () => {
  busy.value = true;
  void fetchData();
};

if (userHandle.value === 'me') {
  if (loggedInUsername === null) {
    window.location.href = '/login?return=/users/me';
  } else {
    userHandle.value = loggedInUsername;
    void router.push({
      params: { userHandle: loggedInUsername },
      hash: location.hash,
    });
  }
}

const triggerModal = () => {
  if (!ownProfile.value) {
    return;
  }

  if (location.hash === '#add-event') {
    showAddEventModal.value = true;
  } else if (location.hash === '#add-content') {
    showAddContentModal.value = true;
  } else if (location.hash === '#link-crowdin') {
    showSetCrowdinUsernameModal.value = true;
  } else {
    return;
  }

  location.hash = '';
};

const loadProfile = async () => {
  try {
    return await fetchUserProfile(userHandle.value, {
      from_date: filterData.value.fromDate,
      to_date: filterData.value.toDate,
    });
  } catch (e) {
    error.value = true;
    isUserNotFound.value = isAxiosError(e) && e.response?.status === HttpStatusCode.NotFound;

    return null;
  }
};

const fetchData = async () => {
  error.value = false;
  isUserNotFound.value = false;
  const profile = await loadProfile();

  if (profile) {
    data.value = profile;
    document.title = `${profile.username} - ${DEFAULT_TITLE}`;
    triggerModal();
    if (route.hash) {
      await nextTick();
      document.querySelector(route.hash)?.scrollIntoView({ behavior: 'smooth' });
    }
  }

  busy.value = false;
};

const bonusPoints = computed(() => data.value?.bonus_points.filter(ScoringUtils.isBonusActivity));
const events = computed(() => data.value?.bonus_points.filter(ScoringUtils.isEngagementActivity));

const totals = computed(() => {
  const scoring = data.value?.scoring;

  return {
    eventsAndContent: ScoringUtils.eventsAndContent(data.value?.bonus_points),
    bonusPointsTotal: ScoringUtils.bonusPointsTotal(data.value?.bonus_points),
    mergedMergeRequests: ScoringUtils.mergedMergeRequests(data.value?.merged_merge_requests, scoring),
    openedMergeRequests: ScoringUtils.openedMergeRequests(data.value?.opened_merge_requests, scoring),
    mergedCommits: ScoringUtils.mergedCommits(data.value?.merged_commits, scoring),
    openedIssues: ScoringUtils.openedIssues(data.value?.opened_issues, scoring),
    addedNotes: ScoringUtils.addedNotes(data.value?.added_notes, scoring),
    translations: ScoringUtils.translations(data.value?.translations),
    discordMessages: ScoringUtils.discordMessages(data.value?.discord_messages, scoring),
    forumPosts: ScoringUtils.forumPosts(data.value?.forum_posts, scoring),
  };
});

const scoringTooltips = computed(() => ScoringUtils.descriptions(data.value?.scoring));

const onCrowdinUsernameSet = (crowdinUsername: string) => {
  if (data.value) {
    data.value.crowdin_username = crowdinUsername;
  }
};
</script>
<template>
  <template v-if="isUserNotFound">
    <div
      class="text-center"
    >
      <img
        :src="SearchSvg"
        alt="Magnifying glass"
      >
      <h2 class="fw-bold">User not found.</h2>
    </div>
    <div>
      If the user has no recent activity, or has not yet been imported, you can view their
      <a
        :href="`https://gitlab.com/${userHandle}`"
        target="_blank"
      >GitLab profile</a>
      directly.
    </div>
  </template>
  <template v-else-if="error">
    <div
      class="text-center"
    >
      <img
        :src="ErrorSvg"
        alt="Red x"
      >
      <h2 class="fw-bold">Something went wrong.</h2>
    </div>
    <div>
      Please try again later.
      If the problem persists, please raise an issue in the
      <a
        href="https://gitlab.com/gitlab-org/developer-relations/contributor-success/contributors-gitlab-com/-/issues/new?issuable_template=default"
        target="_blank"
      >contributors-gitlab-com issue tracker</a>.
    </div>
  </template>
  <template v-else>
    <FilterCard
      v-model="filterData"
      class="my-2"
      @filter="filter"
    />

    <h1 class="d-flex align-items-center justify-content-between my-2">
      <span>
        <ContributorLevelBadge
          :contributor-level="data?.contributor_level"
          :team-member="data?.team_member"
          :core-team="data?.core_team"
        />
        <a
          :href="`https://gitlab.com/${data?.username}`"
          target="_blank"
        >
          {{ data?.username }}
        </a>
      </span>
      <div class="d-flex gap-2 align-items-center">
        <ShareButtons
          v-if="ownProfile"
          :text="profileShareText"
          :url="profileShareUrl"
        />
        <BButton
          v-if="isAdmin && userId"
          variant="outline-primary"
          size="sm"
          :to="{
            name: 'user-edit',
            params: { userId },
          }"
        >
          Edit user
        </BButton>
      </div>
    </h1>

    <div
      v-if="!data?.team_member"
      class="my-2"
    >
      <LevelProgress
        v-if="userId"
        :key="userId"
        :contributor-data="levelProgressData"
      />
    </div>

    <MentorshipCard
      v-if="userId"
      :key="userHandle"
      :is-admin="isAdmin"
      :is-own-profile="ownProfile"
      :user-handle="userHandle"
    />

    <template v-if="data?.rewards">
      <PagedAccordionTable
        :items="data.rewards"
        :fields="rewardFields"
        :busy="busy"
        sort-key="created_at"
        title="Rewards"
      >
        <template #headerButtons>
          <div class="d-inline-flex align-items-center gap-2 me-2">
            <BButton
              v-if="isAdmin"
              variant="outline-primary"
              size="sm"
              class="mx-2"
              @click.stop="showIssueRewardModal = true"
            >
              Issue reward
            </BButton>
            <PointsDocumentationTooltip
              hash="#rewards"
              tooltip="Learn about rewards and the reward store"
              large
              @click.stop
            />
          </div>
        </template>
        <template #cell(issue_web_url)="{ value }">
          <BLink
            is-unsafe-link
            :href="value"
            target="_blank"
          >
            #{{ value.split('/').pop() }}
          </BLink>
        </template>
        <template #cell(awarded_by)="cell">
          <BLink
            is-unsafe-link
            :href="cell.item.awarded_by_web_url"
            target="_blank"
          >
            {{ cell.value }}
          </BLink>
        </template>
        <template #cell(actions)>
          <div class="d-flex justify-content-end">
            <ShareButtons
              :text="'I just earned a reward on the GitLab contributor platform! 🎁'"
              :url="profileShareUrl"
            />
          </div>
        </template>
      </PagedAccordionTable>
    </template>

    <PagedAccordionTable
      :items="events"
      :fields="eventFields"
      :busy="busy"
      :total-points="totals.eventsAndContent"
      sort-key="created_at"
      title="Events & Content"
    >
      <template #headerButtons>
        <div
          v-if="ownProfile"
          class="d-inline-flex flex-wrap justify-content-end gap-2 me-2"
        >
          <BButton
            variant="outline-primary"
            size="sm"
            @click.stop="showAddEventModal = true"
          >
            Add event
          </BButton>
          <BButton
            variant="outline-primary"
            size="sm"
            @click.stop="showAddContentModal = true"
          >
            Add content
          </BButton>
        </div>
      </template>
      <template #cell(reason)="{ value, item }">
        <MarkdownLinkRenderer :value="value" />
        <div
          v-if="item.status === 'rejected' && item.rejection_reason"
          class="text-muted small mt-1"
        >
          Rejected: {{ item.rejection_reason }}
        </div>
      </template>
      <template #cell(status)="{ value }">
        <BBadge :variant="SUBMISSION_STATUS_VARIANT[value] || 'secondary'">
          {{ value }}
        </BBadge>
      </template>
    </PagedAccordionTable>

    <PagedAccordionTable
      :items="bonusPoints"
      :fields="bonusPointsFields"
      :busy="busy"
      :total-points="totals.bonusPointsTotal"
      sort-key="created_at"
      title="Bonus Points"
    >
      <template #headerButtons>
        <BButton
          v-if="isAdmin"
          variant="outline-primary"
          size="sm"
          class="mx-2"
          @click.stop="showAddPointsModal = true"
        >
          Add points
        </BButton>
      </template>
      <template #cell(reason)="{ value }">
        <MarkdownLinkRenderer :value="value" />
      </template>
    </PagedAccordionTable>

    <PagedAccordionTable
      :items="data?.merged_merge_requests"
      :fields="mergedMergeRequestFields"
      :busy="busy"
      :total-points="totals.mergedMergeRequests"
      :scoring-tooltip="scoringTooltips.mergedMergeRequests"
      sort-key="merged_date"
      title="Merged Merge Requests"
    >
      <template #cell(title)="cell">
        <BLink
          is-unsafe-link
          :href="cell.item.web_url"
          target="_blank"
        >
          {{ cell.value }}
        </BLink>
      </template>
      <template #cell(has_linked_issue)="{ value }">
        <span v-if="value">✓</span>
      </template>
    </PagedAccordionTable>

    <PagedAccordionTable
      :items="data?.opened_merge_requests"
      :fields="openedMergeRequestFields"
      :busy="busy"
      :total-points="totals.openedMergeRequests"
      :scoring-tooltip="scoringTooltips.openedMergeRequests"
      sort-key="opened_date"
      title="Created Merge Requests"
    >
      <template #cell(title)="cell">
        <BLink
          is-unsafe-link
          :href="cell.item.web_url"
          target="_blank"
        >
          {{ cell.value }}
        </BLink>
      </template>
      <template #cell(has_linked_issue)="{ value }">
        <span v-if="value">✓</span>
      </template>
      <template #cell(actions)="{ item }">
        <BLink
          v-if="item.state_id === 'opened' && ownProfile"
          :to="{ name: 'manage-merge-request', query: { mergeRequestId: item.id } }"
        >
          Manage
        </BLink>
      </template>
    </PagedAccordionTable>

    <PagedAccordionTable
      :items="data?.merged_commits"
      :fields="commitFields"
      :busy="busy"
      :total-points="totals.mergedCommits"
      :scoring-tooltip="scoringTooltips.mergedCommits"
      sort-key="committed_date"
      title="Commits"
    >
      <template #cell(merge_request_title)="cell">
        <BLink
          is-unsafe-link
          :href="cell.item.merge_request_web_url"
          target="_blank"
        >
          {{ cell.value }}
        </BLink>
      </template>
    </PagedAccordionTable>

    <PagedAccordionTable
      :items="data?.opened_issues"
      :fields="issueFields"
      :busy="busy"
      :total-points="totals.openedIssues"
      :scoring-tooltip="scoringTooltips.openedIssues"
      sort-key="opened_date"
      title="Created Issues"
    >
      <template #cell(title)="cell">
        <BLink
          is-unsafe-link
          :href="cell.item.web_url"
          target="_blank"
        >
          {{ cell.value }}
        </BLink>
      </template>
    </PagedAccordionTable>

    <PagedAccordionTable
      :items="data?.added_notes"
      :fields="noteFields"
      :busy="busy"
      :total-points="totals.addedNotes"
      :scoring-tooltip="scoringTooltips.addedNotes"
      sort-key="added_date"
      title="Added Notes"
    >
      <template #cell(noteable_title)="cell">
        <BLink
          is-unsafe-link
          :href="cell.item.web_url"
          target="_blank"
        >
          {{ cell.value }}
        </BLink>
      </template>
    </PagedAccordionTable>

    <PagedAccordionTable
      :items="data?.translations"
      :fields="translationFields"
      :busy="busy"
      :total-points="totals.translations"
      sort-key="added_date"
      title="Translations"
    >
      <template #headerButtons>
        <div class="d-inline-flex align-items-center gap-2 me-2">
          <BButton
            v-if="canManageCrowdinUsername"
            variant="outline-primary"
            size="sm"
            @click.stop="showSetCrowdinUsernameModal = true"
          >
            Link Crowdin account
          </BButton>
          <PointsDocumentationTooltip
            hash="#translations"
            tooltip="Learn how to earn translation points"
            large
            @click.stop
          />
        </div>
      </template>
    </PagedAccordionTable>

    <PagedAccordionTable
      :items="data?.discord_messages"
      :fields="discordMessageFields"
      :busy="busy"
      :total-points="totals.discordMessages"
      :scoring-tooltip="scoringTooltips.discordMessages"
      sort-key="added_date"
      title="Discord Messages"
    >
      <template #headerButtons>
        <div class="d-inline-flex align-items-center gap-2 me-2">
          <PointsDocumentationTooltip
            hash="#discord-messages"
            tooltip="Learn how to earn Discord points"
            large
            @click.stop
          />
        </div>
      </template>
      <template #cell(added_date)="cell">
        <BLink
          is-unsafe-link
          :href="cell.item.web_url"
          target="_blank"
        >
          {{ cell.value }}
        </BLink>
      </template>
      <template #cell(reply)="{ value }">
        <span v-if="value">✓</span>
      </template>
    </PagedAccordionTable>

    <PagedAccordionTable
      :items="data?.forum_posts"
      :fields="forumPostFields"
      :busy="busy"
      :total-points="totals.forumPosts"
      :scoring-tooltip="scoringTooltips.forumPosts"
      sort-key="added_date"
      title="Forum Posts"
    >
      <template #headerButtons>
        <div class="d-inline-flex align-items-center gap-2 me-2">
          <PointsDocumentationTooltip
            hash="#forum-posts"
            tooltip="Learn how to earn Forum points"
            large
            @click.stop
          />
        </div>
      </template>
      <template #cell(topic)="cell">
        <BLink
          is-unsafe-link
          :href="cell.item.web_url"
          target="_blank"
        >
          {{ cell.value }}
        </BLink>
      </template>
      <template #cell(reply)="{ value }">
        <span v-if="value">✓</span>
      </template>
    </PagedAccordionTable>

    <PagedAccordionTable
      :items="data?.activities"
      :fields="activityFields"
      :busy="busy"
      sort-key="occurred_at"
      title="Activities"
    >
      <template #cell(kind)="{ value }">
        <BBadge>{{ value }}</BBadge>
      </template>
      <template #cell(title)="cell">
        <BLink
          v-if="cell.item.url"
          is-unsafe-link
          :href="cell.item.url"
          target="_blank"
        >
          {{ cell.value }}
        </BLink>
        <template v-else>{{ cell.value }}</template>
      </template>
    </PagedAccordionTable>

    <AddEventModal
      v-if="ownProfile"
      v-model="showAddEventModal"
      @event-added="fetchData"
    />

    <AddContentModal
      v-if="ownProfile"
      v-model="showAddContentModal"
      @content-added="fetchData"
    />

    <AddPointsModal
      v-if="isAdmin && userId"
      v-model="showAddPointsModal"
      :user-id="userId"
      @points-added="fetchData"
    />

    <IssueRewardModal
      v-if="isAdmin && userId"
      v-model="showIssueRewardModal"
      :user-id="userId"
      @reward-issued="fetchData"
    />

    <SetCrowdinUsernameModal
      v-if="canManageCrowdinUsername && userId"
      v-model="showSetCrowdinUsernameModal"
      :user-id="userId"
      :current-username="data?.crowdin_username"
      @crowdin-username-set="onCrowdinUsernameSet"
    />
  </template>
</template>
